import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../core/services/authentication.service';
import {
  CreateNotificationRequest,
  CreateNotificationResponse,
  DeleteNotificationResponse,
  ListNotificationsParams,
  MarkAllNotificationsAsReadResponse,
  NotificationRecord,
  NotificationsHealth,
  PaginatedNotificationsResponse,
  RegisterFcmSubscriptionRequest,
  RegisterFcmSubscriptionResponse,
  UnreadNotificationsCountResponse,
  UnregisterFcmSubscriptionRequest,
  UnregisterFcmSubscriptionResponse
} from '../interfaces/notifications-client';

@Injectable()
export class NotificationsClientService {

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();
  private notificationsRefreshSubject = new Subject<void>();
  public notificationsRefresh$ = this.notificationsRefreshSubject.asObservable();
  private readonly notificationsSyncStorageKey = 'notificationsStateChanged';

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {
    this.listenNotificationChangesAcrossTabs();
  }

  public health(): Observable<NotificationsHealth> {
    return this.http.get<NotificationsHealth>(this.buildUrl('/health'));
  }

  public createNotification(body: CreateNotificationRequest): Observable<CreateNotificationResponse> {
    return this.http.post<CreateNotificationResponse>(this.buildUrl('/notifications'), body);
  }

  public registerFcmSubscription(body: RegisterFcmSubscriptionRequest): Observable<RegisterFcmSubscriptionResponse> {
    return this.http.post<RegisterFcmSubscriptionResponse>(this.buildUrl('/notifications/fcm-subscriptions'), body);
  }

  public unregisterFcmSubscription(body: UnregisterFcmSubscriptionRequest): Observable<UnregisterFcmSubscriptionResponse> {
    return this.http.post<UnregisterFcmSubscriptionResponse>(
      this.buildUrl('/notifications/fcm-subscriptions/unregister'),
      body
    );
  }

  public listByUser(params: ListNotificationsParams): Observable<PaginatedNotificationsResponse> {
    var queryParams = new HttpParams()
      .set('companyId', params.companyId)
      .set('productId', params.productId)
      .set('page', String(params.page || 1))
      .set('pageSize', String(params.pageSize || 10))
      .set('_ts', String(Date.now()));

    if (params.status) {
      queryParams = queryParams.set('status', params.status);
    }

    if (params.cursorCreatedAt && params.cursorId) {
      queryParams = queryParams
        .set('cursorCreatedAt', params.cursorCreatedAt)
        .set('cursorId', params.cursorId);
    }

    return this.http.get<PaginatedNotificationsResponse>(
      this.buildUrl('/notifications/users/' + encodeURIComponent(params.document)),
      { params: queryParams }
    );
  }

  public getUnreadCount(params: Pick<ListNotificationsParams, 'companyId' | 'productId' | 'document'>): Observable<UnreadNotificationsCountResponse> {
    var queryParams = new HttpParams()
      .set('companyId', params.companyId)
      .set('productId', params.productId)
      .set('_ts', String(Date.now()));

    return this.http.get<UnreadNotificationsCountResponse>(
      this.buildUrl('/notifications/users/' + encodeURIComponent(params.document) + '/unread-count'),
      { params: queryParams }
    );
  }

  public markAsRead(id: string): Observable<NotificationRecord> {
    return this.http.patch<NotificationRecord>(
      this.buildUrl('/notifications/' + encodeURIComponent(id) + '/read'),
      {}
    ).pipe(
      tap(() => this.publishNotificationChange())
    );
  }

  public markAllAsRead(params: ListNotificationsParams): Observable<MarkAllNotificationsAsReadResponse> {
    var queryParams = new HttpParams()
      .set('companyId', params.companyId)
      .set('productId', params.productId);

    return this.http.patch<MarkAllNotificationsAsReadResponse>(
      this.buildUrl('/notifications/users/' + encodeURIComponent(params.document) + '/read-all'),
      {},
      { params: queryParams }
    ).pipe(
      tap(() => this.publishNotificationChange())
    );
  }

  public delete(id: string): Observable<DeleteNotificationResponse> {
    return this.http.delete<DeleteNotificationResponse>(
      this.buildUrl('/notifications/' + encodeURIComponent(id))
    ).pipe(
      tap(() => this.publishNotificationChange())
    );
  }

  // =========================================================
  // Contador de no leídas (estado reactivo compartido)
  // =========================================================

  public refreshUnreadCount(): void {
    var scope = this.buildCurrentUserScope();
    if (!scope) {
      this.unreadCountSubject.next(0);
      return;
    }

    this.getUnreadCount(scope).subscribe(
      (response) => {
        var count = response && typeof response.unreadCount === 'number' ? response.unreadCount : 0;
        this.unreadCountSubject.next(count);
      },
      () => {
        // Silencioso: el badge se queda con el último valor conocido.
      }
    );
  }

  public decrementUnreadCount(by: number = 1): void {
    var current = this.unreadCountSubject.getValue();
    var next = Math.max(0, current - by);
    this.unreadCountSubject.next(next);
  }

  public clearUnreadCount(): void {
    this.unreadCountSubject.next(0);
  }

  public getUnreadCountSnapshot(): number {
    return this.unreadCountSubject.getValue();
  }

  public requestNotificationsRefresh(): void {
    this.refreshUnreadCount();
    this.notificationsRefreshSubject.next();
  }

  private listenNotificationChangesAcrossTabs(): void {
    if (typeof window === 'undefined' || !window.addEventListener) return;

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== this.notificationsSyncStorageKey || !event.newValue) return;

      this.refreshUnreadCount();
      this.notificationsRefreshSubject.next();
    });
  }

  private publishNotificationChange(): void {
    try {
      localStorage.setItem(this.notificationsSyncStorageKey, String(Date.now()));
    } catch (error) {
      // Si el navegador bloquea storage, la pestaña actual conserva su estado optimista.
    }
  }

  private buildCurrentUserScope(): { companyId: string; productId: string; document: string } | null {
    var settings = (environment as any).notifications;
    if (!settings || settings.enabled === false || !settings.serverUrl || !settings.productId) {
      return null;
    }

    if (!this.authService || !this.authService.sessionActive()) {
      return null;
    }

    var user = this.authService.getUserSession();
    var document = user && user.information ? user.information.document : null;
    var companyId = user ? user.clientCompanyId : null;

    if (!document || !companyId) return null;

    return { companyId, productId: settings.productId, document };
  }

  private buildUrl(path: string): string {
    var baseUrl = this.getBaseUrl();

    if (!baseUrl) {
      throw new Error('notifications.serverUrl is not configured');
    }

    return baseUrl.replace(/\/$/, '') + path;
  }

  private getBaseUrl(): string {
    var notificationsConfig = (environment as any).notifications;

    return notificationsConfig ? notificationsConfig.serverUrl : '';
  }
}
